import React, { useMemo } from 'react';
import clsx from 'clsx';
import { alpha, Stack } from '@mui/material';
import { useIntl } from 'react-intl';

import { CustomEventProps, WalkinComponentProps } from '../types';
import { LinkedIcon } from '../../../assets/icons';
import { WalkinsIcon } from '../../../assets/DesignAssets/BTL/Icons';
import { removeGlobalCSSVariable, setGlobalCSSVariable } from '../../commons';

import styles from './DayEvent.module.scss';

const HOVER_RELATED_COLORS = {
  borderColor: '#1e3549',
  backgroundColor: '#1e3549',
  dividerColor: alpha('#FFFFFF', 0.15),
  textColor: '#FFFFFF',
};

export function Walkin30({
  title,
  timeRange,
  borderColor,
  backgroundColor,
  serviceBackgroundColor,
  serviceIconColor,
  textColor,
  event,
}: any) {
  return (
    <div
      style={{
        borderColor,
        backgroundColor,
        color: textColor,
      }}
      className={clsx(styles.dayEventRoot, styles.dayEventWalkin)}
      onMouseEnter={() => {
        if (event.splitService) {
          const colorKeys = Object.keys(HOVER_RELATED_COLORS);
          for (let colorKeyIndex = 0; colorKeyIndex < colorKeys.length; colorKeyIndex += 1) {
            const colorKey = colorKeys[colorKeyIndex];

            setGlobalCSSVariable(`--appointment-related-${colorKey}-${event.id}`, HOVER_RELATED_COLORS[colorKey]);
          }
        }
      }}
      onMouseLeave={() => {
        if (event.splitService) {
          const colorKeys = Object.keys(HOVER_RELATED_COLORS);
          for (let colorKeyIndex = 0; colorKeyIndex < colorKeys.length; colorKeyIndex += 1) {
            const colorKey = colorKeys[colorKeyIndex];

            removeGlobalCSSVariable(`--appointment-related-${colorKey}-${event.id}`);
          }
        }
      }}
    >
      <Stack direction="row" spacing={1.5} className={styles.dayEventContent}>
        <Stack direction="row" spacing={0.5}>
          <div className={clsx(styles.dayEventServicesItem)} style={{ backgroundColor: serviceBackgroundColor, color: serviceIconColor }}>
            <WalkinsIcon />
          </div>
          {event.splitService && <LinkedIcon className={styles.linkedIcon30} />}
        </Stack>
        <Stack direction="column" overflow="hidden" spacing={1} flex={1} className={styles.dayEventBody}>
          <Stack direction="row" spacing={1}>
            <p
              className={clsx(
                styles.dayEventBodyText,
                styles.dayEventBodyTextBold,
                styles.dayEventBodyTextNoWrap,
                styles.dayEventBodyTextFullWidth,
              )}
            >
              {title}
            </p>
          </Stack>

          <span className={clsx(styles.dayEventBodyText, styles.dayEventBodyTextSecondary, styles.dayEventTimeRange)}>{timeRange}</span>
        </Stack>
      </Stack>
    </div>
  );
}

export function Walkin15({
  timeRange,
  borderColor,
  backgroundColor,
  serviceBackgroundColor,
  serviceIconColor,
  textColor,
  event,
}: any) {
  return (
    <div
      style={{ borderColor, backgroundColor, color: textColor }}
      className={clsx(styles.dayEventRoot, styles.dayEventRootDense, styles.dayEventWalkin)}
      onMouseEnter={() => {
        if (event.splitService) {
          const colorKeys = Object.keys(HOVER_RELATED_COLORS);
          for (let colorKeyIndex = 0; colorKeyIndex < colorKeys.length; colorKeyIndex += 1) {
            const colorKey = colorKeys[colorKeyIndex];

            setGlobalCSSVariable(`--appointment-related-${colorKey}-${event.id}`, HOVER_RELATED_COLORS[colorKey]);
          }
        }
      }}
      onMouseLeave={() => {
        if (event.splitService) {
          const colorKeys = Object.keys(HOVER_RELATED_COLORS);
          for (let colorKeyIndex = 0; colorKeyIndex < colorKeys.length; colorKeyIndex += 1) {
            const colorKey = colorKeys[colorKeyIndex];

            removeGlobalCSSVariable(`--appointment-related-${colorKey}-${event.id}`);
          }
        }
      }}
    >
      <Stack direction="row" spacing={1.5} className={styles.dayEventContent}>
        <Stack direction="row" spacing={0.5}>
          <div
            className={clsx(styles.dayEventServicesItem, styles.dayEventServicesItemDense)}
            style={{ backgroundColor: serviceBackgroundColor, color: serviceIconColor }}
          >
            <WalkinsIcon />
          </div>
          {event.splitService && <LinkedIcon className={styles.linkedIcon15} />}
        </Stack>

        <Stack direction="row" spacing={1.5} flex={1} alignItems="center" className={clsx([styles.dayEventBody, styles.dayEventBodyDense])}>
          <span
            className={clsx(
              styles.dayEventBodyText,
              styles.dayEventBodyTextSecondary,
              styles.dayEventBodyTextFullWidth,
              styles.dayEventTimeRange,
            )}
          >
            {timeRange}
          </span>
        </Stack>
      </Stack>
    </div>
  );
}

export function Walkin10({
  timeRange,
  borderColor,
  backgroundColor,
  serviceBackgroundColor,
  serviceIconColor,
  textColor,
  event,
}: any) {
  return (
    <div
      style={{ borderColor, backgroundColor, color: textColor }}
      className={clsx(styles.dayEventRoot, styles.dayEventRootUltraDense, styles.dayEventWalkin)}
      onMouseEnter={() => {
        if (event.splitService) {
          const colorKeys = Object.keys(HOVER_RELATED_COLORS);
          for (let colorKeyIndex = 0; colorKeyIndex < colorKeys.length; colorKeyIndex += 1) {
            const colorKey = colorKeys[colorKeyIndex];

            setGlobalCSSVariable(`--appointment-related-${colorKey}-${event.id}`, HOVER_RELATED_COLORS[colorKey]);
          }
        }
      }}
      onMouseLeave={() => {
        if (event.splitService) {
          const colorKeys = Object.keys(HOVER_RELATED_COLORS);
          for (let colorKeyIndex = 0; colorKeyIndex < colorKeys.length; colorKeyIndex += 1) {
            const colorKey = colorKeys[colorKeyIndex];

            removeGlobalCSSVariable(`--appointment-related-${colorKey}-${event.id}`);
          }
        }
      }}
    >
      <Stack direction="row" spacing={1} className={styles.dayEventContent} alignItems="center">
        <div
          className={clsx(styles.dayEventServicesItem, styles.dayEventServicesItemNano)}
          style={{ backgroundColor: serviceBackgroundColor, color: serviceIconColor }}
        >
          <WalkinsIcon />
        </div>
        <span className={clsx(styles.dayEventBodyText, styles.dayEventBodyTextSecondary, styles.dayEventTimeRange)}>{timeRange}</span>
      </Stack>
    </div>
  );
}

export function DayEventWalkin({ event }: CustomEventProps) {
  const intl = useIntl();

  const DayEventComponent = useMemo(() => {
    const duration = event.duration || 10;

    if (duration >= 30) {
      return Walkin30;
    }

    if (duration >= 15) {
      return Walkin15;
    }

    return Walkin10;
  }, [event.duration]);

  const timeRange = useMemo(() => `${event.startFormatted} - ${event.endFormatted}`, [event]);

  const colorProps = useMemo(() => {
    const colors = {
      backgroundColor: '#ffffff',
    };

    if (event.splitService) {
      const colorKeys = Object.keys(HOVER_RELATED_COLORS);
      for (let colorKeyIndex = 0; colorKeyIndex < colorKeys.length; colorKeyIndex += 1) {
        const colorKey = colorKeys[colorKeyIndex];

        colors[colorKey] = `var(--appointment-related-${colorKey}-${event.id}, ${colors[colorKey]})`;
      }
    }

    return ({
      ...colors,
      serviceBackgroundColor: 'rgb(201 224 240)',
      serviceIconColor: '#000000',
    });
  }, [event]);

  return (
    <DayEventComponent title={intl.formatMessage({ id: 'calendar.walkinTitle' })} timeRange={timeRange} event={event} {...colorProps} />
  );
}
